/**
 * MediaPipe Face Mesh mouth landmark indices (468-point topology).
 * Outer lips form the visible lip border; inner lips bound the oral opening.
 */

/** Outer lip silhouette (clockwise-ish around the mouth) */
export const OUTER_LIP_INDICES = [
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37,
  39, 40, 185,
] as const;

/** Inner lip / mouth opening */
export const INNER_LIP_INDICES = [
  78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310, 311, 312, 13, 82,
  81, 80, 191,
] as const;

/** All mouth-related indices used for smile region extraction */
export const MOUTH_LANDMARK_INDICES = [
  ...new Set([...OUTER_LIP_INDICES, ...INNER_LIP_INDICES]),
] as const;

/** Image-left / image-right outer commissures (MediaPipe Face Mesh). */
export const MOUTH_COMMISSURE_LEFT = 61;
export const MOUTH_COMMISSURE_RIGHT = 291;
/** Inner mouth corners */
export const INNER_MOUTH_LEFT = 78;
export const INNER_MOUTH_RIGHT = 308;
export const INNER_UPPER_LIP_CENTER = 13;
export const INNER_LOWER_LIP_CENTER = 14;
/** Upper vermillion–tooth border, left → right (excludes corners). */
export const INNER_UPPER_LIP_INDICES = [
  191, 80, 81, 82, 13, 312, 311, 310, 415,
] as const;
/** Facial midline landmarks */
export const PHILTRUM_INDEX = 164;
export const NOSE_TIP_INDEX = 1;
export const CHIN_INDEX = 152;

/** Padding around smile bounding box when cropping (relative to box size).
 * Larger values give the tooth detector more smile context / lateral teeth. */
export const SMILE_CROP_PADDING = 0.55;

/** Extra horizontal / vertical padding ratios for smile crop (moderate — too much face skin hurts accuracy) */
export const SMILE_CROP_PADDING_X = 0.38;
export const SMILE_CROP_PADDING_Y = 0.28;

/** Upscale lip crop for tooth detection when narrower than this (pixels) */
export const TOOTH_DETECT_MIN_WIDTH = 640;

/** Max upscale factor for tooth detection resolution */
export const TOOTH_DETECT_MAX_SCALE = 2;

/** CDN paths pinned to the installed package major version */
export const MEDIAPIPE_WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

export const FACE_LANDMARKER_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

/**
 * FDI upper arch, left → right on a frontal photo
 * (patient's right 18…11 | patient's left 21…28).
 * Matches case-uploader ToothSelector layout.
 */
export const UPPER_ARCH_FDI_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11] as const;
export const UPPER_ARCH_FDI_LEFT = [21, 22, 23, 24, 25, 26, 27, 28] as const;
export const UPPER_ARCH_FDI = [
  ...UPPER_ARCH_FDI_RIGHT,
  ...UPPER_ARCH_FDI_LEFT,
] as const;
export const UPPER_ARCH_TOOTH_COUNT = UPPER_ARCH_FDI.length; // 16
